import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ServiceType } from '../../types/api';
import BlacklistModal, { getFlowerBlacklistItems, getFruitBlacklistItems } from './BlacklistModal';
import OptimizedImage from '../common/OptimizedImage';

interface BlacklistSectionProps {
  preferences: Record<string, boolean>;
  onChange: (preferences: Record<string, boolean>) => void;
  serviceType?: ServiceType;
}

export const BlacklistSection: React.FC<BlacklistSectionProps> = ({
  preferences,
  onChange,
  serviceType = 'flower'
}) => {
  const { t } = useTranslation();
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);

  // Log preferences when component mounts or preferences change
  useEffect(() => {
    console.log('🔍 BlacklistSection - Preferences received:', preferences);
    console.log('🔍 BlacklistSection - Service type:', serviceType);
    console.log('🔍 BlacklistSection - Blacklisted items:', getBlacklistedItems());
  }, [preferences, serviceType]);

  // Get blacklisted items from preferences
  const getBlacklistedItems = (): string[] => {
    const blacklistedItems = Object.keys(preferences).filter(key => 
      key.startsWith('blacklist_') && preferences[key]
    );
    console.log('📝 getBlacklistedItems - Found items:', blacklistedItems);
    return blacklistedItems;
  };

  // Get blacklist item details including image
  const getBlacklistItemDetails = (itemId: string) => {
    const allBlacklistItems = [
      ...getFlowerBlacklistItems(t), 
      ...getFruitBlacklistItems(t)
    ];
    const item = allBlacklistItems.find(option => option.id === itemId);
    
    if (item) {
      return {
        label: item.label,
        image: item.image
      };
    }
    
    // Fallback if item not found
    return {
      label: itemId.replace('blacklist_', '').replace(/_/g, ' '),
      image: undefined
    };
  };

  // Handle blacklist selection change
  const handleBlacklistChange = (blacklistedItems: string[]) => {
    console.log('🔄 handleBlacklistChange - New blacklisted items:', blacklistedItems);
    console.log('🔄 handleBlacklistChange - Current preferences before change:', preferences);
    
    const newPreferences = { ...preferences };
    
    // Remove all existing blacklist items
    Object.keys(newPreferences).forEach(key => {
      if (key.startsWith('blacklist_')) {
        console.log('🗑️ Removing existing blacklist item:', key);
        delete newPreferences[key];
      }
    });
    
    // Add new blacklist items
    blacklistedItems.forEach(itemId => {
      console.log('➕ Adding blacklist item:', itemId);
      newPreferences[itemId] = true;
    });
    
    console.log('✅ handleBlacklistChange - Final preferences to save:', newPreferences);
    onChange(newPreferences);
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
        {serviceType === 'fruit' 
          ? t('subscription.fruitPreferences.groupBlacklist', 'Never Include (Blacklist)')
          : t('subscription.preferences.groupBlacklist', 'Never Include (Blacklist)')}
      </h3>
      
      <div className="space-y-2">
        <div 
          className="border rounded-lg p-3 cursor-pointer transition-all duration-200 border-gray-200 bg-white hover:border-gray-300"
          onClick={() => setShowBlacklistModal(true)}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 p-2 rounded-full bg-red-100 text-red-600">
              <Plus size={16} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  {serviceType === 'fruit'
                    ? t('subscription.fruitPreferences.addBlacklistItems', 'Add Fruits to Blacklist')
                    : t('subscription.preferences.addBlacklistItems', 'Add Flowers to Blacklist')
                  }
                </h4>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {serviceType === 'fruit'
                  ? t('subscription.fruitPreferences.blacklistNote', 'Items in this section will never be included in any delivery. This is a strict filter.')
                  : t('subscription.preferences.blacklistNote', 'Items in this section will never be included in any delivery. This is a strict filter.')
                }
              </p>
              
              {getBlacklistedItems().length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {getBlacklistedItems().map((itemId) => {
                    const itemDetails = getBlacklistItemDetails(itemId);
                    return (
                      <div
                        key={itemId}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white border-gray-200 text-black flex items-center gap-1"
                      >
                        {itemDetails.image && (
                          <OptimizedImage 
                            src={typeof itemDetails.image === 'string' ? itemDetails.image : itemDetails.image.png}
                            srcWebp={typeof itemDetails.image === 'string' ? undefined : itemDetails.image.webp}
                            srcAvif={typeof itemDetails.image === 'string' ? undefined : itemDetails.image.avif}
                            alt={itemDetails.label}
                            className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                            loading="lazy"
                            width={20}
                            height={20}
                          />
                        )}
                        <span>{itemDetails.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {getBlacklistedItems().length === 0 && (
                <p className="text-xs text-gray-500 italic mt-2">
                  {serviceType === 'fruit'
                    ? t('subscription.fruitPreferences.noBlacklistItems', 'No items blacklisted')
                    : t('subscription.preferences.noBlacklistItems', 'No items blacklisted')
                  }
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Blacklist Modal */}
      <BlacklistModal
        isOpen={showBlacklistModal}
        onClose={() => setShowBlacklistModal(false)}
        selectedItems={getBlacklistedItems()}
        onSelectionChange={handleBlacklistChange}
        serviceType={serviceType}
      />
    </div>
  );
};

export default BlacklistSection; 